// src/polaris-components/OnboardingWelcome.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { verifyBillingSession, BillingVerificationResponse } from '../utils/subscriptionApi';
import '../styles/subscription-result.css';

export const OnboardingWelcome: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [subscriptionDetails, setSubscriptionDetails] = useState<BillingVerificationResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      verifySession(sessionId);
    } else {
      setError('No session ID found. Please check your email for the signup link.');
      setIsLoading(false);
    }
  }, [searchParams]);

  const verifySession = async (sessionId: string) => {
    try {
      const data = await verifyBillingSession(sessionId);
      setSubscriptionDetails(data);
    } catch (err: any) {
      setError(err.message || 'Failed to verify payment. Please contact support.');
      console.error('Payment verification error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatPrice = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2
    }).format(amount / 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="subscription-result-container">
        <div className="result-loading">
          <div className="spinner"></div>
          <p>Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subscription-result-container">
        <div className="result-card error-card">
          <div className="result-icon error-icon">⚠️</div>
          <h1>Payment Verification Failed</h1>
          <p className="result-message">{error}</p>
          <div className="result-actions">
            <a
              href="mailto:support@polarisathlete.com"
              className="result-btn secondary-btn"
            >
              Contact Support
            </a>
            <button
              onClick={() => navigate('/')}
              className="result-btn primary-btn"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-result-container">
      <div className="result-card success-card">
        <div className="result-icon success-icon">✓</div>
        <h1>Payment Successful!</h1>
        <p className="welcome-title">Welcome to Polaris Athlete Hub</p>

        {subscriptionDetails && (
          <>
            <div className="subscription-summary">
              <div className="summary-item">
                <span className="summary-label">Plan</span>
                <span className="summary-value">{subscriptionDetails.planName}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Billing Cycle</span>
                <span className="summary-value">
                  {formatPrice(subscriptionDetails.amount, subscriptionDetails.currency)} / {subscriptionDetails.billingInterval}
                </span>
              </div>
              {subscriptionDetails.nextPaymentDate && (
                <div className="summary-item">
                  <span className="summary-label">Next Payment</span>
                  <span className="summary-value">
                    {formatDate(subscriptionDetails.nextPaymentDate)}
                  </span>
                </div>
              )}
              {subscriptionDetails.trialEnd && (
                <div className="summary-item trial-info">
                  <span className="summary-label">Trial Period</span>
                  <span className="summary-value">
                    Ends {formatDate(subscriptionDetails.trialEnd)}
                  </span>
                </div>
              )}
            </div>

            <div className="email-notice">
              <p>📧 Check your email for login instructions and account details</p>
            </div>

            <div className="next-steps">
              <h2>Getting Started</h2>
              <ul>
                <li>Log in with your credentials sent to your email</li>
                <li>Complete your athlete profile for personalized recommendations</li>
                <li>Explore college planning tools and build your target list</li>
                <li>Access NIL education resources and exclusive content</li>
                <li>Connect with coaches and track your athletic journey</li>
              </ul>
            </div>
          </>
        )}

        <div className="result-actions">
          <button
            onClick={() => navigate('/login')}
            className="result-btn primary-btn"
          >
            Continue to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};